import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, Switch } from 'antd';
import CustomEditor from '../custom-editor';

const { Option } = Select;

interface EditTemplateProps {
  visible: boolean;
  templateData: any;
  modalitiesList: any[];
  bodyPartsList: any[];
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const EditTemplate: React.FC<EditTemplateProps> = ({
  visible,
  templateData,
  modalitiesList,
  bodyPartsList,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [templateHtml, setTemplateHtml] = useState('');
  useEffect(() => {
    if (visible && templateData) {
      // Reset form with template data
      form.setFieldsValue({
        rt_template_name: templateData.rt_template_name,
        rt_display_name: templateData.rt_display_name,
        rt_modality: templateData.rt_modality,
        rt_bodypart: templateData.rt_bodypart,
        rt_comments: templateData.rt_comments,
        rt_active: templateData.rt_deleted === 'N',
      });
      setTemplateHtml(templateData.template_html || '');
    } else if (!visible) {
      // Reset form when modal closes
      form.resetFields();
      setTemplateHtml('');
    }
  }, [visible, templateData, form]);

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then(values => {
        const submitData = {
          ...values,
          rt_active: values.rt_active ? 'Y' : 'N',
          rt_deleted: values.rt_active ? 'N' : 'Y',
          template_html: templateHtml,
        };
        onSubmit(submitData);
      })
      .catch(errorInfo => {
        console.warn('Validation failed:', errorInfo);
      });
  }, [form, templateHtml, onSubmit]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setTemplateHtml('');
    onCancel();
  }, [form, onCancel]);

  return (
    <Modal
      title="Edit Template"
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          className="cancel-btn"
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          className="submit-btn"
        >
          Update Template
        </Button>,
      ]}
      className="edit-template-modal"
    >
      <Form
        form={form}
        layout="vertical"
        className="edit-template-form"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Template Name"
              name="rt_template_name"
              rules={[
                {
                  required: true,
                  message: 'Please enter template name!',
                },
              ]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Display Name"
              name="rt_display_name"
              rules={[
                {
                  required: true,
                  message: 'Please enter display name!',
                },
              ]}
            >
              <Input placeholder="Enter display name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Modality"
              name="rt_modality"
              rules={[
                {
                  required: true,
                  message: 'Please select modality!',
                },
              ]}
            >
              <Select placeholder="Select modality">
                {modalitiesList.map(modality => (
                  <Option
                    key={modality.code}
                    value={modality.code}
                  >
                    {modality.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Body Part"
              name="rt_bodypart"
              rules={[
                {
                  required: true,
                  message: 'Please select body part!',
                },
              ]}
            >
              <Select placeholder="Select body part">
                {bodyPartsList.map(bodyPart => (
                  <Option
                    key={bodyPart.code}
                    value={bodyPart.code}
                  >
                    {bodyPart.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={18}>
            <Form.Item
              label="Comments"
              name="rt_comments"
            >
              <Input.TextArea
                rows={3}
                placeholder="Enter any comments or notes about this template"
                maxLength={200}
                showCount
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Active Status"
              name="rt_active"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Template Content">
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}>
            <CustomEditor
              initialContent={templateHtml}
              handleChange={useCallback(html => setTemplateHtml(html), [])}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditTemplate;
